import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check } from "lucide-react";
import "./TeamPawsPersonalityPage.css";

import progressIcon from "../../assets/progressIcon5.png";
import nextIcon from "../../assets/nextButtonIcon.png";
import nextIcon2 from "../../assets/nextButtonIcon2.png";

import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";
import DesktopTopBar from "../../components/DesktopTopBar";
import DesktopBottomBar from "../../components/DesktopBottomBar";

const TeamPawsPersonalityPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const promptRefs = useRef({});

  const dogName = formData.dogName || "Dog";
  const formattedDogName =
    dogName.charAt(0).toUpperCase() + dogName.slice(1).toLowerCase();

  const [mobileStep, setMobileStep] = useState(
    location.state?.returnToStep || 1,
  );

  const personalityTraits = [
    "Playful clown",
    "Chill couch potato",
    "Shy wallflower",
    "Confident social butterfly",
    "Curious explorer",
    "Affectionate snuggler",
    "Independent thinker",
  ];

  const personalityPrompts = [
    `One of ${formattedDogName}'s quirks is`,
    "My dog is so sweet when",
    "The inspo for my dog's name is",
    "My dog's coolest party trick is",
    "The weirdest thing my dog has eaten is",
  ];

  useEffect(() => {
    const existingTraits =
      formData.selectedTraits || formData.dogTraits || formData.traits;
    if (existingTraits && Array.isArray(existingTraits)) {
      const custom = existingTraits.find((t) => !personalityTraits.includes(t));
      setFormData((prev) => ({
        ...prev,
        selectedTraits: existingTraits,
        dogTraits: existingTraits,
        customTrait: custom || prev.customTrait || "",
      }));
    }

    const savedPersonality = formData.dogPersonality;
    if (Array.isArray(savedPersonality) && savedPersonality.length > 0) {
      const newRawAnswers = {};
      const newPersonalityObj = {};
      let activeIdx = null;
      let customQ = "";
      let customA = "";

      savedPersonality.forEach((str) => {
        if (typeof str !== "string") return;
        let matched = false;

        personalityPrompts.forEach((promptText, idx) => {
          const genericPrompt = promptText.replace(formattedDogName, "").trim();
          if (
            str.toLowerCase().startsWith(promptText.toLowerCase()) ||
            str.toLowerCase().startsWith(genericPrompt.toLowerCase())
          ) {
            let answer = str.substring(promptText.length).trim();
            if (!answer && genericPrompt) {
              const matchIdx = str
                .toLowerCase()
                .indexOf(genericPrompt.toLowerCase());
              if (matchIdx !== -1) {
                answer = str.substring(matchIdx + genericPrompt.length).trim();
              }
            }
            newRawAnswers[idx] = answer;
            newPersonalityObj[idx] = `${promptText} ${answer}`.trim();
            if (activeIdx === null) activeIdx = idx;
            matched = true;
          }
        });

        if (!matched && str.trim()) {
          activeIdx = "custom";
          const parts = str.split("?");
          if (parts.length > 1) {
            customQ = parts[0] + "?";
            customA = parts.slice(1).join("?").trim();
          } else {
            customQ = "Custom:";
            customA = str;
          }
          newPersonalityObj["custom"] = str;
        }
      });

      setFormData((prev) => {
        const mergedObj = {
          ...(prev.dogPersonalityObj || {}),
          ...newPersonalityObj,
        };
        return {
          ...prev,
          dogPersonalityObj: mergedObj,
          dogPersonality: Object.values(mergedObj).filter(Boolean),
          rawAnswers: { ...(prev.rawAnswers || {}), ...newRawAnswers },
          selectedPromptIndex:
            prev.selectedPromptIndex !== undefined
              ? prev.selectedPromptIndex
              : activeIdx,
          customPromptQuestion: customQ || prev.customPromptQuestion || "",
          customPromptAnswer: customA || prev.customPromptAnswer || "",
        };
      });
    }
  }, []);

  // Auto-scroll inside the newly constrained flex box
  useEffect(() => {
    const activeIndex = formData.selectedPromptIndex;
    if (activeIndex !== undefined && activeIndex !== null) {
      setTimeout(() => {
        promptRefs.current[activeIndex]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [formData.selectedPromptIndex]);

  const toggleTrait = (item) => {
    const currentList = formData.selectedTraits || [];
    const newList = currentList.includes(item)
      ? currentList.filter((i) => i !== item)
      : [...currentList, item];

    setFormData((prev) => ({
      ...prev,
      selectedTraits: newList,
      dogTraits: newList,
    }));
  };

  const handleCustomTrait = (value) => {
    const currentList = formData.selectedTraits || [];
    const oldValue = formData.customTrait || "";

    let updatedList = currentList.filter((item) => item !== oldValue);

    if (value.trim() !== "") {
      updatedList = [...updatedList, value];
    }

    setFormData((prev) => ({
      ...prev,
      selectedTraits: updatedList,
      dogTraits: updatedList,
      customTrait: value,
    }));
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePromptAnswerChange = (index, promptText, answerValue) => {
    const combinedValue = answerValue ? `${promptText} ${answerValue}` : "";
    setFormData((prev) => {
      const existingObj = prev.dogPersonalityObj || {};
      const updatedPersonalityObj = {
        ...existingObj,
        ...{ [index]: combinedValue },
      };
      if (!combinedValue) {
        delete updatedPersonalityObj[index];
      }
      return {
        ...prev,
        dogPersonalityObj: updatedPersonalityObj,
        dogPersonality: Object.values(updatedPersonalityObj).filter(Boolean),
        rawAnswers: {
          ...(prev.rawAnswers || {}),
          [index]: answerValue,
        },
      };
    });
  };

  const handleCustomPromptChange = (question, answer) => {
    const combinedCustom = question || answer ? `${question} ${answer}` : "";
    setFormData((prev) => {
      const existingObj = prev.dogPersonalityObj || {};
      const updatedPersonalityObj = {
        ...existingObj,
        custom: combinedCustom,
      };
      if (!combinedCustom) {
        delete updatedPersonalityObj["custom"];
      }
      return {
        ...prev,
        customPromptQuestion: question,
        customPromptAnswer: answer,
        dogPersonalityObj: updatedPersonalityObj,
        dogPersonality: Object.values(updatedPersonalityObj).filter(Boolean),
      };
    });
  };

  const hasDefaultAnswers = Object.values(formData.rawAnswers || {}).some(
    (val) => typeof val === "string" && val.trim().length > 0,
  );
  const hasCustomAnswer =
    Boolean(formData.customPromptQuestion?.trim()) &&
    Boolean(formData.customPromptAnswer?.trim());

  const isStep1Valid = formData.selectedTraits?.length >= 1;
  const isStep2Valid = hasDefaultAnswers || hasCustomAnswer;
  const isFinalNextEnabled = isStep1Valid && isStep2Valid;

  const handleDesktopNext = () => {
    if (isFinalNextEnabled) navigate("/dimsum-availability");
  };

  const handleSkip = () => navigate("/dimsum-availability");

  return (
    <div
      className={`tp-container ${mobileStep === 1 ? "tp-mobile-step-1" : "tp-mobile-step-2"}`}
    >
      {/* LEFT PANEL: Traits (Mobile Step 1) */}
      <div
        className={`tp-panel tp-left-panel ${mobileStep === 1 ? "tp-mobile-active" : "tp-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={() => setMobileStep(2)}
          backColor="#000"
          logoColor="#009fd9"
          blueLogo="true"
          isProtectedRoute={false}
        />

        <DesktopTopBar
          showBack={true}
          onBack={() => navigate("/dog-personality")}
          backColor="#000"
          showProgress={true}
          progressIconSrc={progressIcon}
          progressPercent={60}
          iconLeft={52}
          iconWidth="80px"
        />

        <div className="tp-content-wrapper">
          <div className="tp-header-section mobile-only">
            <div className="tp-dog-icon-container">
              <img
                src={progressIcon}
                alt="Progress"
                className="tp-progress-icon"
              />
            </div>
            <div className="tp-progress-track">
              <div className="tp-progress-fill tp-step-1-fill"></div>
            </div>
          </div>

          <h1 className="tp-title" style={{ textAlign: "left" }}>
            DESCRIBE {dogName.toUpperCase()}'S IN 3 WORDS.
          </h1>
          <p
            className="tp-subtitle"
            style={{ textAlign: "left", marginBottom: "1.5rem" }}
          >
            Give us a glimpse into their pawsonality!
          </p>

          <textarea
            className="tp-personality-textarea"
            placeholder="Goofy, cuddly, adventurous..."
            value={(formData.selectedTraits || []).join(", ")}
            onChange={(e) => {
              const val = e.target.value;
              const arr = val ? [val] : [];
              setFormData((prev) => ({
                ...prev,
                selectedTraits: arr,
                dogTraits: arr,
              }));
            }}
          />

          <MobileBottomBar
            onNext={() =>
              navigate("/dimsum-availability", { state: { returnToStep: 2 } })
            }
            isNextEnabled={isStep1Valid}
            nextIconSrc={nextIcon2}
            variant="blue"
          />
        </div>
      </div>

      {/* RIGHT PANEL: Personality Prompts (Mobile Step 2) */}
      <div
        className={`tp-panel tp-right-panel ${
          window.innerWidth >= 1024 || mobileStep === 2
            ? "tp-mobile-active"
            : "tp-mobile-hidden"
        }`}
      >
        <MobileTopBar
          onBack={() => navigate("/dog-personality", { state: { step: 1 } })}
          backColor="#fff"
          logoColor="#fff"
          isProtectedRoute={false}
        />

        <div
          className={`tp-content-wrapper tp-right-content ${
            window.innerHeight >= 850
              ? "tp-prompts-wrapper"
              : "tp-prompts-wrapper-small"
          }`}
        >
          <div className="tp-header-section tp-mobile-only">
            <div className="tp-dog-icon-container">
              <img
                src={progressIcon}
                alt="Progress"
                className="tp-progress-icon"
              />
            </div>
            <div className="tp-progress-track">
              <div className="tp-progress-fill tp-step-2-fill"></div>
            </div>
          </div>

          <h2 className="tp-right-title tp-text-white">
            {dogName.toUpperCase()}'S PERSONALITY
          </h2>
          <p className="tp-subtitle tp-text-light-blue">
            Pick a prompt - Show off your pup's personality
          </p>

          <div className="tp-prompts-list">
            {personalityPrompts.map((prompt, index) => {
              const isSelected = formData.selectedPromptIndex === index;
              const rawVal = formData.rawAnswers?.[index] || "";

              return (
                <div
                  key={index}
                  ref={(el) => (promptRefs.current[index] = el)}
                  className={`tp-prompt-item ${isSelected ? "active" : ""}`}
                  onClick={() => updateField("selectedPromptIndex", index)}
                >
                  <label className="tp-prompt-label">
                    {prompt} {!isSelected && !rawVal ? "...." : ""}
                  </label>
                  {isSelected ? (
                    <input
                      type="text"
                      className="tp-prompt-input"
                      placeholder="Enter here"
                      autoFocus
                      value={rawVal}
                      onChange={(e) =>
                        handlePromptAnswerChange(index, prompt, e.target.value)
                      }
                    />
                  ) : rawVal ? (
                    <div className="tp-saved-answer">{rawVal}</div>
                  ) : (
                    <div className="tp-underline"></div>
                  )}
                </div>
              );
            })}

            {formData.selectedPromptIndex === "custom" ? (
              <div
                className="tp-custom-prompt-container"
                ref={(el) => (promptRefs.current["custom"] = el)}
              >
                <div className="tp-custom-group">
                  <label>Your Question:</label>
                  <input
                    type="text"
                    className="tp-prompt-input"
                    placeholder="e.g. Favorite toy..."
                    value={formData.customPromptQuestion || ""}
                    onChange={(e) =>
                      handleCustomPromptChange(
                        e.target.value,
                        formData.customPromptAnswer || "",
                      )
                    }
                  />
                </div>
                <div className="tp-custom-group">
                  <label>Your Answer:</label>
                  <input
                    type="text"
                    className="tp-prompt-input"
                    placeholder="Enter answer here"
                    value={formData.customPromptAnswer || ""}
                    onChange={(e) =>
                      handleCustomPromptChange(
                        formData.customPromptQuestion || "",
                        e.target.value,
                      )
                    }
                  />
                </div>
              </div>
            ) : (
              <div
                className="tp-write-own-btn"
                onClick={() => updateField("selectedPromptIndex", "custom")}
              >
                Write your own
              </div>
            )}
          </div>

          <DesktopBottomBar
            onNext={handleDesktopNext}
            isNextEnabled={isFinalNextEnabled}
            nextIconSrc={nextIcon}
            showSkip={true}
            onSkip={handleSkip}
            isSkipBlue={true}
            variant="green"
          />

          <MobileBottomBar
            onNext={() => setMobileStep(1)}
            isNextEnabled={isStep2Valid}
            nextIconSrc={nextIcon}
            showSkip={true}
            onSkip={() => setMobileStep(1)}
            variant="green"
          />
        </div>
      </div>
    </div>
  );
};

export default TeamPawsPersonalityPage;
